import { useTranslation } from 'react-i18next';
import { useComments } from 'entities/comment';
import { useState, useRef, useLayoutEffect, useEffect, useCallback, useMemo } from 'react';
import { useEditorContext } from 'app/state/contexts/EditorContext';
import { useSidebar } from 'app/state/contexts/SidebarContext';
import { useUser } from '../../../../state/contexts/UserContext';
import { MentionInput } from 'app/components/MentionInput';
import { v4 as uuidv4 } from 'uuid';

import { useDelayedSave } from 'app/utils/hooks/delayed-save';
import { CommentHeader } from './CommentHeader';
import { Response } from './Response';
import { findCitationWithContext } from 'slate/utils';
import { useSlate } from 'slate-react';
import { Node, Element } from 'slate';
import { useEditorLiveSection, useEditorUiSetters } from 'app/state/contexts/EditorUiContext';
import { scrollToCommentAnchor } from './comment-jump';
import { CommentItem } from './types';

interface CommentProps {
  comment: CommentItem;
  responses?: CommentItem[];
  activeResponse?: CommentItem | null;
  setActiveResponse?: (value: CommentItem | null) => void;
  readOnly?: boolean;
}

export const Comment = ({ comment, responses, activeResponse, setActiveResponse = () => {}, readOnly = false }: CommentProps) => {
  const { t } = useTranslation(['comments', 'common']);

  const [menuOpen, setMenuOpen] = useState(false);
  const [currentComment, setCurrentComment] = useState<CommentItem>(comment);
  const [isUpdating, setIsUpdating] = useState(false);
  const [responseComment, setResponseComment] = useState<CommentItem | null>(null);
  const [showResponses, setShowResponses] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const editor = useSlate();
  const { superId: sectionSuperId, content: sectionContent } = useEditorLiveSection();
  const { setDraftComment, draftComment, setActiveComment, activeComment } = useSidebar();
  const { user } = useUser();
  const { shadowHost } = useEditorContext();
  const { deleteComment, updateCommentStatus, publishComment, createCommentAsync, updateCommentMessageAsync, refetchComments } = useComments(sectionSuperId);
  const { setIsDirty } = useEditorUiSetters();
  const isActiveComment = activeComment?.id === comment.id;
  const isResponse = comment.onType === 'comment';
  const isNewComment = draftComment?.id === comment.id;
  const isUniqueText = useMemo(() => {
    for (const [node] of Node.nodes(editor)) {
      if (
        Element.isElement(node) &&
        (node.type === 'paragraph' || node.type === 'custom-formatting' || node.type === 'list-item') &&
        node.uuid === comment.onId
      ) {
        const textContent = Node.string(node);
        const occurences = findCitationWithContext(textContent, comment.citation ?? '');
        return occurences.length <= 1;
      }
    }
    return true;
  }, [comment, editor, sectionContent]);
  const mentionedResponseCount = useMemo(() => {
    const mention = `@${user?.firstName ?? ''}${user?.lastName ?? ''}`;
    if (!user?.firstName && !user?.lastName) return 0;
    return (responses ?? []).filter((response) => response.publishedAt && !response.closedAt && response.message?.includes(mention)).length;
  }, [responses, user?.firstName, user?.lastName]);

  const generateResponseComment = useCallback(() => {
    return {
      id: uuidv4(),
      userId: user?.id,
      onId: (activeComment ?? comment).id,
      onType: 'comment',
      citation: '',
      message: '',
      sectionSuperId,
      createdAt: new Date(),
      updatedAt: null,
      publishedAt: null,
      user: {
        firstName: user?.firstName,
        lastName: user?.lastName,
        email: user?.email,
      },
    };
  }, [activeComment, comment, sectionSuperId, user?.email, user?.firstName, user?.id, user?.lastName]);

  useEffect(() => {
    const newResponse = generateResponseComment();
    setResponseComment(newResponse);
  }, [comment, comment.id, generateResponseComment]);

  const handleDelete = () => {
    const element = shadowHost?.shadowRoot?.querySelector(`#anchor-${comment.onId}`);
    element?.parentElement?.classList.remove('layout-leaf-comment');
    deleteComment(comment.id);
  };

  useEffect(() => {
    if (isActiveComment) setShowResponses(true);
  }, [isActiveComment]);

  useLayoutEffect(() => {
    if (isNewComment && !comment.updatedAt && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.selectionStart = inputRef.current.value.length;
    }
  }, [comment.publishedAt, comment.updatedAt, isNewComment, comment]);

  const saveComment = async (_message?: string) => {
    setIsDirty(true);
    if (comment.updatedAt) {
      await updateCommentMessageAsync({ commentId: comment.id, message: currentComment.message ?? '' });
    } else {
      const newComment = { ...comment, message: currentComment.message, publishedAt: null };
      await createCommentAsync({ comment: newComment });
    }
    setIsDirty(false);
  };
  const [save, cancel] = useDelayedSave(saveComment, 1500);

  const handleChange = (e: string) => {
    cancel();
    setCurrentComment((prev: CommentItem) => ({ ...prev, message: e }));
    save(e);
  };

  const handleSubmit = async (e: React.MouseEvent) => {
    cancel();
    e.preventDefault();
    await saveComment(currentComment.message);
    publishComment({ commentId: currentComment.id, publish: true });
    setIsUpdating(false);
    setDraftComment(null);
    setActiveComment(null);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isNewComment) {
      deleteComment(currentComment.id);
    }
    setDraftComment(null);
    setIsUpdating(false);
    setActiveComment(null);
  };

  const actionHandler = (action: string, event: React.MouseEvent) => {
    event.stopPropagation();
    switch (action) {
      case 'edit':
        setIsUpdating(true);
        break;
      case 'delete':
        handleDelete();
        break;
      case 'open':
      case 'close':
        updateCommentStatus({ commentId: comment.id, status: action !== 'open' });
        break;
      default:
        return;
    }
    setMenuOpen(false);
  };
  const renderCommentState = () => {
    if (comment.isOrphaned) {
      return `${t('comments:anchor-deleted')} - `;
    } else if (comment.citationDeletedAt) {
      return `${t('comments:no-citation')} - `;
    } else if (!isUniqueText) {
      return <span className="comment--warning">{t('comments:duplicate-citation')} </span>;
    }
    return null;
  };

  return (
    <div
      data-comment-id={comment.id}
      className={`${isActiveComment ? 'active' : ''} ${!comment.publishedAt ? 'draft' : ''} ${comment.closedAt ? 'archived' : ''} comment`}
      onClick={() => {
        setActiveComment(comment);
        scrollToCommentAnchor(shadowHost, comment);
        setShowResponses(true);
      }}
    >
      <span className="comment--citation">
        {renderCommentState()}
        {`"${formatCitation(comment.citation ?? '')}"`}
      </span>
      <CommentHeader
        isActiveComment={isActiveComment}
        comment={comment}
        isUpdating={isUpdating}
        setMenuOpen={setMenuOpen}
        menuOpen={menuOpen}
        actionHandler={actionHandler}
        readOnly={readOnly}
      />
      {isNewComment || isUpdating ? (
        <>
          <MentionInput value={currentComment.message ?? ''} onChange={handleChange} ref={inputRef} placeHolder={t('comments:message.placeholder')} />

          <div className="comment--buttons">
            <button onClick={handleCancel} className="btn btn-ghost btn-md">
              {t('common:button.cancel')}
            </button>
            <button
              onClick={async () => {
                cancel();
                await saveComment(currentComment.message);
                publishComment({ commentId: currentComment.id, publish: false });
                setDraftComment(null);
                setIsUpdating(false);
                refetchComments();
              }}
              className="btn btn-secondary btn-md "
            >
              {t('common:save-draft')}
            </button>
            <button onClick={handleSubmit} className="btn btn-primary btn-md">
              {t('common:publish')}
            </button>
          </div>
        </>
      ) : (
        <div className="comment--message">{formatMessage(comment.message ?? '')}</div>
      )}
      {(responses?.length ?? 0) > 0 && (
        <button
          className="not-a-button comment--response-button"
          onClick={(e) => {
            e.stopPropagation();
            setShowResponses(!showResponses);
          }}
        >
          {comment.responses?.length} {t('comments:response')}
          {mentionedResponseCount > 0 && <span className="comment--response-mention-count">{mentionedResponseCount}</span>}
        </button>
      )}
      {showResponses && responses && (
        <div className="comment--response--wrapper">
          {responses?.map((response) => {
            return (
              <Response
                key={response.id}
                response={response}
                isActiveResponse={activeResponse}
                setActiveResponse={setActiveResponse}
                sectionSuperId={sectionSuperId}
                setNewResponseComment={() => setResponseComment(generateResponseComment())}
                readOnly={readOnly}
              />
            );
          })}
        </div>
      )}
      {!readOnly && isActiveComment && !draftComment && !isUpdating && !isResponse && !comment.closedAt && comment.publishedAt && (
        <div className="comment--response--wrapper">
          {responseComment && (
            <Response
              key={responseComment.id}
              response={responseComment}
              isActiveResponse={activeResponse}
              setActiveResponse={setActiveComment}
              sectionSuperId={sectionSuperId}
              setNewResponseComment={() => setResponseComment(generateResponseComment())}
            />
          )}
        </div>
      )}
    </div>
  );
};

const formatMessage = (text: string) => {
  const parts = text.split(/(@\w+)/g);
  return parts.map((part, index) =>
    part.startsWith('@') ? (
      <span key={index} className="comment--mention">
        {part}
      </span>
    ) : (
      part
    )
  );
};

const formatCitation = (text: string) => {
  if (text.length > 35) {
    return text.slice(0, 35) + '...';
  }
  return text;
};
